/**
 * React component for unit selection display.
 * Shows unit information based on selection state (single, multiple, or empty).
 */

import { useEffect, useState } from 'react';
import { useUnitSelectionStore } from '@shared/stores';
import { getUnitDefinition, UnitType } from '@shared/constants/units';
import UnitIcon from './UnitIcon';

interface UnitSelectionDisplayProps {
  scene: unknown | null;
}

interface UnitData {
  id: string;
  unitType: UnitType;
  health: number;
  maxHealth: number;
  stats: {
    baseDamage: number;
    armor: number;
    attackRange: number;
    movementSpeed: number;
  };
}

export default function UnitSelectionDisplay({ scene }: UnitSelectionDisplayProps) {
  const selectedUnitIds = useUnitSelectionStore((state) => state.selectedUnitIds);
  const setSelectedUnits = useUnitSelectionStore((state) => state.setSelectedUnits);
  const [units, setUnits] = useState<UnitData[]>([]);

  useEffect(() => {
    if (!scene || typeof scene !== 'object' || scene === null) {
      setUnits([]);
      return;
    }

    const sceneWithMethods = scene as {
      getUnitManager?: () => {
        getUnit: (id: string) =>
          | {
              getId: () => string;
              getUnitType: () => UnitType;
              getHealth: () => number;
              getMaxHealth: () => number;
              getStats: () => {
                baseDamage: number;
                armor: number;
                attackRange: number;
                movementSpeed: number;
              };
            }
          | undefined;
      } | null;
    };

    const unitManager = sceneWithMethods.getUnitManager?.();
    if (!unitManager) {
      setUnits([]);
      return;
    }

    // Update unit data function
    const updateUnitData = () => {
      // Get unit data for selected units
      const unitData: UnitData[] = [];
      for (const unitId of selectedUnitIds) {
        const unit = unitManager.getUnit(unitId);
        if (unit) {
          const stats = unit.getStats();
          unitData.push({
            id: unit.getId(),
            unitType: unit.getUnitType(),
            health: unit.getHealth(),
            maxHealth: unit.getMaxHealth(),
            stats: {
              baseDamage: stats.baseDamage,
              armor: stats.armor,
              attackRange: stats.attackRange,
              movementSpeed: stats.movementSpeed,
            },
          });
        }
      }
      setUnits(unitData);
    };

    // Initial update
    updateUnitData();

    // Poll for health updates (update every 50ms to keep health bars in sync)
    const interval = setInterval(updateUnitData, 50);

    return () => {
      clearInterval(interval);
    };
  }, [scene, selectedUnitIds]);

  // Empty state
  if (units.length === 0) {
    return (
      <div className="text-gray-400 text-sm" data-unit-selection>
        No units selected
      </div>
    );
  }

  // Single unit selected
  if (units.length === 1) {
    const unit = units[0];
    const definition = getUnitDefinition(unit.unitType);

    return (
      <div className="flex flex-col gap-2" data-unit-selection>
        <div className="flex items-center gap-3">
          <UnitIcon
            unitType={unit.unitType}
            health={unit.health}
            maxHealth={unit.maxHealth}
            size="large"
          />
          <div className="flex-1">
            <div className="text-white font-semibold">{definition.name}</div>
            <div className="text-gray-300 text-sm">
              Health: {unit.health}/{unit.maxHealth}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm text-gray-300">
          <div>Damage: {unit.stats.baseDamage}</div>
          <div>Armor: {unit.stats.armor}</div>
          <div>Range: {unit.stats.attackRange}</div>
          <div>Speed: {unit.stats.movementSpeed}</div>
        </div>
      </div>
    );
  }

  // Multiple units selected
  const handleUnitClick = (unitId: string) => {
    // Select only this unit in the store
    setSelectedUnits([unitId]);

    // Also update the selection in the Phaser scene
    if (scene && typeof scene === 'object' && scene !== null) {
      const sceneWithMethods = scene as {
        getUnitSelectionController?: () => {
          setSelectedUnits?: (unitIds: string[]) => void;
        } | null;
      };

      const selectionController = sceneWithMethods.getUnitSelectionController?.();
      if (selectionController?.setSelectedUnits) {
        selectionController.setSelectedUnits([unitId]);
      }
    }
  };

  return (
    <div className="flex flex-wrap gap-2 items-start" data-unit-selection>
      {units.map((unit) => (
        <UnitIcon
          key={unit.id}
          unitType={unit.unitType}
          health={unit.health}
          maxHealth={unit.maxHealth}
          size="small"
          clickable={true}
          onClick={() => handleUnitClick(unit.id)}
        />
      ))}
    </div>
  );
}
